import React from 'react';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import { SectionHeader } from '@havenengineering/module-shared-owners-ui/dist/components/SectionHeader/SectionHeader';

import { buttonClickGTMEvent } from '../../../helpers/googleTag';
import { DrainDownOption } from '../DrainDown.enum';
import Option from '../Option';
import Packages, { Package } from '../Packages';
import Terms from '../Terms';
import styles from './OptionSelect.module.scss';

export const options: DrainDownOptionData[] = [
  {
    title: 'Non-Central Heating',
    price: 65,
    includes: ['Drain Down and reconnection', 'Haven Frost-Free Guarantee'],
    type: DrainDownOption.basic,
  },
  {
    title: 'Central Heating',
    price: 100,
    includes: [
      'Drain Down and reconnection',
      'Haven Frost-Free Guarantee',
      'Inhibitor top-up from -10deg to -15deg',
    ],
    notes: [
      '*Inhibitor top-ups for less than -5deg will be charged at £23.50 per litre until restored to -10deg. (-10deg to -15deg is free of charge)',
    ],
    type: DrainDownOption.heating,
  },
  {
    title: 'Twin Unit (Lodge)',
    price: 115,
    includes: [
      'Drain Down and reconnection',
      'Haven Frost-Free Guarantee',
      'Inhibitor top-up from -10deg to -15deg',
    ],
    notes: [
      '*Inhibitor top-ups for less than -5deg will be charged at £23.50 per litre until restored to -10deg. (-10deg to -15deg is free of charge)',
    ],
    type: DrainDownOption.lodge,
  },
];

const selfOrganizedCaravan: DrainDownOptionData = {
  type: DrainDownOption.diyCaravan,
};
const selfOrganizedLodge: DrainDownOptionData = {
  type: DrainDownOption.diyLodge,
};

const OptionSelect = ({
  parkCode,
  onStepChange,
  onFormChange,
}: DrainDownStepProps) => {
  const handleChoice = (value: DrainDownOption, packageOption?: Package) => {
    onFormChange({ target: { name: 'option', value, packageOption } });

    if (packageOption) {
      buttonClickGTMEvent({
        linkText: `Choose ${packageOption}`,
      });
    }

    if (
      value === DrainDownOption.diyLodge ||
      value === DrainDownOption.diyCaravan
    ) {
      buttonClickGTMEvent({ linkText: "i'm organising my own" });
    }

    onStepChange();
  };

  return (
    <div className={styles.root}>
      <SectionHeader title="Choose a Drain Down service" />
      <p className="owners-body">
        With winter coming, it&apos;s important to book a Drain Down to ensure
        your holiday home is prepared for the colder months. We offer a Drain
        Down service to suit every holiday home, giving you peace of mind during
        the winter season.
      </p>
      <InlineMessaging
        type={InlineMessagingTheme.INFO_THEME}
        title="Haven Frost Free Guarantee"
        message="All Drain Down services include the repair of any damaged pipework,
          plumbing fittings, sanitary-ware, washing machine, dishwasher,
          heating, and boiler parts if they get frost damage* while your van is
          drained down."
        dismissible={false}
      />
      <section className={styles.section} data-testid="packageSection">
        <SectionHeader title="Find the right Drain Down service for you" />

        <Packages parkCode={parkCode as string} handleChoice={handleChoice} />
      </section>
      <section className={styles.section}>
        <SectionHeader title="Options" />
        <div className={styles.optionContainer}>
          {options.map((option, index) => (
            <div key={index}>
              <Option option={option} onChoose={handleChoice} />
            </div>
          ))}
        </div>
      </section>
      <section className={styles.section}>
        <SectionHeader title="Alternatively" />
        <div className={styles.optionContainer}>
          <div>
            <Option option={selfOrganizedCaravan} onChoose={handleChoice} />
          </div>
          <div>
            <Option option={selfOrganizedLodge} onChoose={handleChoice} />
          </div>
        </div>
      </section>
      <Terms />
    </div>
  );
};

export default OptionSelect;
